import { useState, type ComponentProps } from 'react';
import { Box, Container, Stack } from '@mui/material';
import {
  AgronodAlert,
  AgronodButton,
  AgronodCard,
  AgronodChip,
  AgronodIcon,
  AgronodTypography,
  LoaderLinear,
} from '@agronod/mui-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToken } from '../../utils/hooks';
import {
  Inriktning,
  INRIKTNING_LABEL,
  KundAtkomst,
  MyAccessResponse,
  verifieringApi,
} from '../../utils/api';
import { DataskyddDialog } from './DataskyddDialog';

const rollLabel = (roll: KundAtkomst['roll']) =>
  roll === 'REVISIONSLEDARE' ? 'Revisionsledare' : 'Revisor';

const datum = (iso: string) => iso.slice(0, 10);

type IconName = ComponentProps<typeof AgronodIcon>['name'];

/** Inriktningens ikon i designsystemet (AgronodIcon) – en per inriktning. */
const INRIKTNING_IKON: Record<Inriktning, IconName> = {
  gris: 'gris',
};

/**
 * Ett uppdrag i listan: kund + beräkningsår + inriktning – samma skopning
 * som revisionen (RevisionScope). Ett kort per kombination, så att varje
 * kort har en enda "Visa".
 */
interface Uppdrag {
  kund: KundAtkomst;
  ar: number;
  inriktning: Inriktning;
  /** Uppdragsperioden pågår och året är kundens senaste – annars "Äldre uppdrag". */
  aktivt: boolean;
}

const uppdragFor = (kunder: KundAtkomst[], idag: Date): Uppdrag[] =>
  kunder.flatMap((kund) => {
    const periodPagar = new Date(kund.giltigTom) >= idag;
    const senasteAr = Math.max(...kund.atkomligaAr);
    return kund.atkomligaAr.flatMap((ar) =>
      kund.atkomligaInriktningar.map((inriktning) => ({
        kund,
        ar,
        inriktning,
        aktivt: periodPagar && ar === senasteAr,
      })),
    );
  });

const InriktningChip = ({ inriktning }: { inriktning: Inriktning }) => (
  <Stack
    direction="row"
    spacing={0.75}
    sx={(theme) => ({
      alignItems: 'center',
      pl: 1,
      pr: 1.5,
      py: 0.5,
      borderRadius: '24px',
      backgroundColor: theme.palette.dataViz.coral[100],
      whiteSpace: 'nowrap',
    })}
  >
    <AgronodIcon
      name={INRIKTNING_IKON[inriktning]}
      sx={(theme) => ({ fontSize: 20, color: theme.palette.dataViz.coral[700] })}
    />
    <AgronodTypography variant="body2">
      {INRIKTNING_LABEL[inriktning]}
    </AgronodTypography>
  </Stack>
);

const UppdragKort = ({
  uppdrag,
  onOppna,
}: {
  uppdrag: Uppdrag;
  onOppna?: () => void;
}) => {
  const { kund, ar, inriktning, aktivt } = uppdrag;
  return (
    <AgronodCard
      onClick={onOppna}
      sx={(theme) => ({
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
        flexWrap: 'wrap',
        opacity: aktivt ? 1 : 0.6,
        ...(onOppna && {
          cursor: 'pointer',
          transition: 'border-color 150ms linear',
          '&:hover': { borderColor: theme.palette.gray400 },
        }),
      })}
    >
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 260 }}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
        >
          <AgronodTypography variant="h6">
            {kund.kundNamn ?? kund.kundId}
          </AgronodTypography>
          <InriktningChip inriktning={inriktning} />
          {kund.endastPseudonymiserat && (
            <AgronodChip size="small" label="Urval utan identitet" />
          )}
        </Stack>
        <AgronodTypography
          variant="body2"
          sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
        >
          Uppdragsperiod {datum(kund.giltigFrom)} – {datum(kund.giltigTom)} ·
          Beräkningsår {ar}
        </AgronodTypography>
      </Stack>
      <Stack spacing={1} sx={{ alignItems: 'flex-end' }}>
        {onOppna && (
          <AgronodButton variant="contained" onClick={onOppna}>
            Visa
          </AgronodButton>
        )}
        <AgronodTypography
          variant="caption"
          sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
        >
          Din roll: {rollLabel(kund.roll)}
        </AgronodTypography>
      </Stack>
    </AgronodCard>
  );
};

/**
 * Verifieringsuppdrag (spec flöde 6.1): användarens kunder ur /me/access
 * med härledd roll, uppdragsperiod, beräkningsår och inriktning – ett kort
 * per kund, år och inriktning (designprototyp v4). Uppdrag vars period
 * gått ut, eller äldre år än kundens senaste, ligger under "Äldre uppdrag"
 * utan knapp. Kunden visas med namnet som sparats vid uppsättningen i
 * backoffice; id:t visas bara som reserv när namnet saknas (äldre uppdrag).
 */
const KundvalPage = () => {
  const { accessToken } = useToken();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // AS-4231: kund och mål som väntar på godkännande av dataskyddsvillkoren.
  const [vantande, setVantande] = useState<{
    kund: KundAtkomst;
    mal: string;
  } | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['me-access'],
    queryFn: () => verifieringApi.getMyAccess(accessToken!),
    enabled: !!accessToken,
  });

  const godkann = useMutation({
    mutationFn: (kundId: string) =>
      verifieringApi.godkannDataskydd(accessToken!, kundId),
    onSuccess: (_, kundId) => {
      // Flaggan sätts i cachen innan navigeringen, så att revisionssidans
      // spärr ser godkännandet direkt; servern är sanningen vid nästa hämtning.
      queryClient.setQueryData<MyAccessResponse>(['me-access'], (gammal) =>
        gammal && {
          ...gammal,
          kunder: gammal.kunder.map((k) =>
            k.kundId === kundId ? { ...k, dataskyddGodkant: true } : k,
          ),
        },
      );
      // Stängde användaren dialogen medan anropet pågick är godkännandet ändå
      // sparat (och cachen uppdaterad), men ingen navigering sker.
      const mal = vantande?.mal;
      setVantande(null);
      if (mal) {
        navigate(mal);
      }
    },
  });

  const oppna = ({ kund, ar, inriktning }: Uppdrag) => {
    const mal = `/kund/${encodeURIComponent(kund.kundId)}/ar/${ar}/${inriktning}/revision`;
    if (kund.dataskyddGodkant) {
      navigate(mal);
    } else {
      godkann.reset();
      setVantande({ kund, mal });
    }
  };

  if (!accessToken || isPending) {
    return <LoaderLinear color="primary" isGlobal={true} />;
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ pt: 4 }}>
        <AgronodAlert severity="error">{error.message}</AgronodAlert>
      </Container>
    );
  }

  if (data.kunder.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ pt: 8, textAlign: 'center' }}>
        <AgronodTypography variant="h4" sx={{ mb: 2 }}>
          Ingen aktiv åtkomst
        </AgronodTypography>
        <AgronodTypography variant="body1" sx={{
          color: "text.secondary"
        }}>
          Ert bolag saknar aktivt verifieringsuppdrag, eller så har
          uppdragsperioden gått ut. Kontakta Agronod om detta inte stämmer.
        </AgronodTypography>
      </Container>
    );
  }

  const alla = uppdragFor(data.kunder, new Date());
  const aktiva = alla.filter((u) => u.aktivt);
  const aldre = alla.filter((u) => !u.aktivt);
  // Kunder utan konfigurerade år eller inriktningar ger inga kort – visas som rad.
  const ejKonfigurerade = data.kunder.filter(
    (k) => k.atkomligaAr.length === 0 || k.atkomligaInriktningar.length === 0,
  );
  const kortNyckel = (u: Uppdrag) => `${u.kund.kundId}-${u.ar}-${u.inriktning}`;

  return (
    <Container maxWidth="md" sx={{ pt: 5, pb: 8 }}>
      <Box sx={{ mb: 3 }}>
        <AgronodTypography variant="h4" sx={{ mb: 1 }}>
          Verifieringsuppdrag
        </AgronodTypography>
        <AgronodTypography variant="body1" sx={{
          color: "text.secondary"
        }}>
          Alla aktiva uppdrag visas här.
        </AgronodTypography>
      </Box>
      <Stack spacing={2}>
        {aktiva.map((u) => (
          <UppdragKort key={kortNyckel(u)} uppdrag={u} onOppna={() => oppna(u)} />
        ))}
        {aktiva.length === 0 && (
          <AgronodTypography variant="body2" sx={{ color: 'text.secondary' }}>
            Inga pågående verifieringsuppdrag.
          </AgronodTypography>
        )}
        {ejKonfigurerade.map((kund) => (
          <AgronodCard key={kund.kundId} sx={{ p: 3 }}>
            <AgronodTypography variant="h6" sx={{ mb: 0.5 }}>
              {kund.kundNamn ?? kund.kundId}
            </AgronodTypography>
            <AgronodTypography variant="body2" sx={{ color: 'text.secondary' }}>
              Inga åtkomliga beräkningsår eller inriktningar är konfigurerade
              ännu.
            </AgronodTypography>
          </AgronodCard>
        ))}
      </Stack>

      {aldre.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Box sx={{ mb: 2 }}>
            <AgronodTypography variant="h5" sx={{ mb: 0.5 }}>
              Äldre uppdrag
            </AgronodTypography>
            <AgronodTypography variant="body2" sx={{ color: 'text.secondary' }}>
              Uppdrag vars period har gått ut eller som gäller ett tidigare
              beräkningsår. De kan inte längre öppnas.
            </AgronodTypography>
          </Box>
          <Stack spacing={2}>
            {aldre.map((u) => (
              <UppdragKort key={kortNyckel(u)} uppdrag={u} />
            ))}
          </Stack>
        </Box>
      )}

      <DataskyddDialog
        open={vantande !== null}
        upptagen={godkann.isPending}
        fel={godkann.error?.message ?? null}
        onClose={() => setVantande(null)}
        onGodkann={() => vantande && godkann.mutate(vantande.kund.kundId)}
      />
    </Container>
  );
};

export default KundvalPage;
